import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  OVERSEAS_CURRENCIES,
  OVERSEAS_RATES,
  buildOverseasBreakdownRows,
  buildOverseasCopyText,
  calculateOverseasServiceFee,
  formatPercent,
  formatTwd,
  formatTwdNumber,
  markupPercent,
  nrTaxDivisorLabel,
  toPositiveNumber,
  type FeeMode,
} from '../utils/overseasServiceCalculator';

type CopyState = 'idle' | 'ok' | 'fail';

const OverseasServiceCalculator: React.FC = () => {
  const [curIdx, setCurIdx] = useState(0);
  const [customCode, setCustomCode] = useState('');
  const [amount, setAmount] = useState('1000');
  const [rate, setRate] = useState('31.5');
  const [feeMode, setFeeMode] = useState<FeeMode>('card');
  const [wireFee, setWireFee] = useState('800');
  const [taxOn, setTaxOn] = useState(true);
  const [serviceRate, setServiceRate] = useState<number>(OVERSEAS_RATES.service[0]);
  const [flash, setFlash] = useState(false);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const prevTotal = useRef<number | null>(null);
  const flashTimer = useRef<number | null>(null);
  const copyTimer = useRef<number | null>(null);

  const currency = OVERSEAS_CURRENCIES[curIdx];
  const isCustom = currency.code === 'CUSTOM';
  const code = isCustom ? (customCode.trim().toUpperCase() || '自訂') : currency.code;
  const amountNum = toPositiveNumber(amount);
  const rateNum = toPositiveNumber(rate);

  const result = useMemo(
    () =>
      calculateOverseasServiceFee({
        amount,
        rate,
        feeMode,
        wireFee,
        taxOn,
        serviceRate,
      }),
    [amount, rate, feeMode, wireFee, taxOn, serviceRate]
  );

  const rows = useMemo(
    () =>
      buildOverseasBreakdownRows(result, {
        code,
        amount: amountNum,
        rate: rateNum,
        feeMode,
        taxOn,
        serviceRate,
      }),
    [result, code, amountNum, rateNum, feeMode, taxOn, serviceRate]
  );

  const markup = markupPercent(result.total, result.base);

  useEffect(() => {
    if (prevTotal.current !== null && prevTotal.current !== result.total) {
      setFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setFlash(false), 280);
    }
    prevTotal.current = result.total;
    return () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    };
  }, [result.total]);

  useEffect(() => {
    return () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    };
  }, []);

  const handleCurrencySelect = useCallback((index: number) => {
    const next = OVERSEAS_CURRENCIES[index];
    setCurIdx(index);
    if (next.rate != null) {
      setRate(String(next.rate));
    }
  }, []);

  const toggleTax = useCallback(() => {
    setTaxOn((on) => !on);
  }, []);

  const handleTaxKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        toggleTax();
      }
    },
    [toggleTax]
  );

  const copyFallback = useCallback((text: string): boolean => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.cssText = 'position:fixed;top:-1000px;opacity:0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  }, []);

  const handleCopy = useCallback(async () => {
    const text = buildOverseasCopyText(
      { code, amt: amountNum, r: rateNum, total: result.total, markup },
      rows
    );
    const done = (ok: boolean) => {
      setCopyState(ok ? 'ok' : 'fail');
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopyState('idle'), 2500);
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        done(true);
        return;
      }
      throw new Error('no clipboard api');
    } catch {
      try {
        done(copyFallback(text));
      } catch {
        done(false);
      }
    }
  }, [amountNum, code, copyFallback, markup, rateNum, result.total, rows]);

  const resultNote = `${code} ${formatTwdNumber(amountNum)} @ ${rateNum}${
    taxOn ? ' · 含稅還原' : ' · 免稅'
  }${feeMode === 'card' ? ' · 卡費' : feeMode === 'wire' ? ' · 匯費' : ''} · 服務費 ${formatPercent(serviceRate)}`;

  return (
    <div className="mx-auto max-w-[560px]">
      <div className="overflow-hidden rounded-[14px] border-[0.5px] border-[#e0e0ec] bg-white">
        <div className="flex items-center gap-3.5 bg-primary px-4 py-5 sm:px-6">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[10px] bg-white p-1.5">
            <img src="/Logo.svg" alt="" className="h-full w-full" />
          </div>
          <div>
            <h1 className="m-0 text-base font-medium text-white">境外服務收費計算機</h1>
            <p className="mt-0.5 text-xs text-white/70">稅金僅對貨款課徵；手續費／匯費不課稅</p>
          </div>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <div className="mb-2 text-[11px] font-medium tracking-wider text-[#999]">幣別</div>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {OVERSEAS_CURRENCIES.map((item, index) => {
              const pressed = index === curIdx;
              return (
                <button
                  key={item.code}
                  type="button"
                  aria-pressed={pressed}
                  onClick={() => handleCurrencySelect(index)}
                  className={`rounded-full px-3.5 py-1 text-[13px] transition-all ${
                    pressed
                      ? 'border-[1.5px] border-primary bg-primary text-white'
                      : 'border-[0.5px] border-[#d0d0e0] bg-white text-ci-text'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {isCustom && (
            <div className="mb-3">
              <label htmlFor="overseas-code" className="mb-1.5 block text-xs font-medium text-[#888]">
                幣別代碼
              </label>
              <input
                id="overseas-code"
                maxLength={8}
                placeholder="例如 SGD、AUD"
                value={customCode}
                onChange={(event) => setCustomCode(event.target.value)}
                className="w-full rounded-lg border-[0.5px] border-[#d0d0e0] bg-white px-2.5 py-2 text-sm text-ci-text shadow-none outline-none focus:border-primary focus:ring-0"
              />
            </div>
          )}

          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="overseas-amt" className="mb-1.5 block text-xs font-medium text-[#888]">
                購買金額（{code}）
              </label>
              <div className="relative flex items-center">
                {currency.symbol ? (
                  <span className="pointer-events-none absolute left-2.5 text-xs leading-none text-[#aaa]">
                    {currency.symbol}
                  </span>
                ) : null}
                <input
                  id="overseas-amt"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={0.01}
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  className={`w-full rounded-lg border-[0.5px] border-[#d0d0e0] bg-white py-2 pr-2.5 text-sm text-ci-text shadow-none outline-none focus:border-primary focus:ring-0 ${
                    currency.symbol ? 'pl-[38px]' : 'pl-2.5'
                  }`}
                />
              </div>
            </div>
            <div>
              <label htmlFor="overseas-rate" className="mb-1.5 block text-xs font-medium text-[#888]">
                匯率（{code} → TWD）
              </label>
              <div className="relative flex items-center">
                <span className="pointer-events-none absolute left-2.5 text-[10px] leading-none text-[#aaa]">
                  TWD
                </span>
                <input
                  id="overseas-rate"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={0.0001}
                  value={rate}
                  onChange={(event) => setRate(event.target.value)}
                  className="w-full rounded-lg border-[0.5px] border-[#d0d0e0] bg-white py-2 pl-[38px] pr-2.5 text-sm text-ci-text shadow-none outline-none focus:border-primary focus:ring-0"
                />
              </div>
              <div className="mt-0.5 text-[11px] text-[#bbb]">參考匯率，請以實際入帳為準</div>
            </div>
          </div>

          <hr className="my-4 border-0 border-t-[0.5px] border-[#ebebf0]" />

          <div className="mb-2 text-[11px] font-medium tracking-wider text-[#999]">附加費用（二擇一）</div>
          <div className="mb-2.5 flex flex-wrap gap-1">
            {(
              [
                { value: 'none', label: '無' },
                { value: 'card', label: '信用卡手續費 3%' },
                { value: 'wire', label: '匯費（固定）' },
              ] as const
            ).map((item) => {
              const pressed = feeMode === item.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  aria-pressed={pressed}
                  onClick={() => setFeeMode(item.value)}
                  className={`min-w-[60px] flex-1 rounded-md px-2 py-1.5 text-xs transition-all ${
                    pressed
                      ? 'border border-primary bg-primary/10 font-bold text-primary'
                      : 'border-[0.5px] border-[#d0d0e0] bg-white text-[#777]'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {feeMode === 'wire' && (
            <div className="mb-3">
              <label htmlFor="overseas-wire" className="mb-1.5 block text-xs font-medium text-[#888]">
                匯費金額（TWD）
              </label>
              <div className="relative flex items-center">
                <span className="pointer-events-none absolute left-2.5 text-[10px] leading-none text-[#aaa]">
                  TWD
                </span>
                <input
                  id="overseas-wire"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={1}
                  value={wireFee}
                  onChange={(event) => setWireFee(event.target.value)}
                  className="w-full rounded-lg border-[0.5px] border-[#d0d0e0] bg-white py-2 pl-[38px] pr-2.5 text-sm text-ci-text shadow-none outline-none focus:border-primary focus:ring-0"
                />
              </div>
            </div>
          )}

          <div className="mb-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <div className="rounded-[10px] border-[0.5px] border-[#ebebf0] px-3 py-2.5">
              <div className="mb-1.5 text-[11px] font-medium tracking-wider text-[#999]">非居住者稅</div>
              <div
                id="overseas-tax-switch"
                role="switch"
                tabIndex={0}
                aria-checked={taxOn}
                onClick={toggleTax}
                onKeyDown={handleTaxKeyDown}
                className="flex cursor-pointer select-none items-center justify-between gap-2 text-[13px]"
              >
                <span>{taxOn ? `適用（÷ ${nrTaxDivisorLabel()}）` : '不適用'}</span>
                <span
                  className={`relative h-[21px] w-[38px] flex-shrink-0 rounded-[11px] transition-colors ${
                    taxOn ? 'bg-primary' : 'bg-[#dcdce6]'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-[17px] w-[17px] rounded-full bg-white shadow-sm transition-transform ${
                      taxOn ? 'translate-x-[19px]' : 'translate-x-0.5'
                    }`}
                  />
                </span>
              </div>
            </div>
            <div className="rounded-[10px] border-[0.5px] border-[#ebebf0] px-3 py-2.5">
              <div className="mb-1.5 text-[11px] font-medium tracking-wider text-[#999]">服務費</div>
              <div className="flex flex-wrap gap-1">
                {OVERSEAS_RATES.service.map((rateOption) => {
                  const pressed = serviceRate === rateOption;
                  return (
                    <button
                      key={rateOption}
                      type="button"
                      aria-pressed={pressed}
                      onClick={() => setServiceRate(rateOption)}
                      className={`min-w-[60px] flex-1 rounded-md px-2 py-1.5 text-xs transition-all ${
                        pressed
                          ? 'border border-primary bg-primary/10 font-bold text-primary'
                          : 'border-[0.5px] border-[#d0d0e0] bg-white text-[#777]'
                      }`}
                    >
                      {formatPercent(rateOption)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mb-2 text-[11px] font-medium tracking-wider text-[#999]">費用計算步驟</div>
          <div className="mb-3.5 overflow-hidden rounded-[10px] border-[0.5px] border-[#ebebf0]">
            {rows.map((row, index) => (
              <div
                key={`${row.label}-${index}`}
                className={`flex items-center gap-2.5 border-b-[0.5px] border-[#f0f0f5] px-3.5 py-2 last:border-b-0 sm:flex-nowrap ${
                  row.isSub ? 'bg-[#f3f3f9] font-bold' : index % 2 === 1 ? 'bg-[#fafafa]' : 'bg-white'
                }`}
              >
                <div className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-primary/12 text-[11px] font-medium text-primary">
                  {row.isSub ? '=' : row.add ? '+' : index + 1}
                </div>
                <div className="min-w-0 flex-1 text-[13px] text-[#666]">
                  {row.label}
                  {row.note && <div className="mt-px text-[11px] text-[#b4b4c0]">{row.note}</div>}
                </div>
                <div className="whitespace-nowrap text-right font-mono text-[13px] text-ci-text">
                  {formatTwdNumber(row.val)}
                </div>
              </div>
            ))}
          </div>

          <div
            className={`mb-0 flex items-center justify-between gap-3 rounded-[10px] border border-primary/30 px-5 py-4 transition-colors max-[480px]:flex-col max-[480px]:items-start ${
              flash ? 'bg-primary/[0.16]' : 'bg-primary/[0.07]'
            }`}
          >
            <div>
              <div className="text-[13px] font-medium text-primary">應收金額</div>
              <div className="mt-0.5 text-[11px] text-[#aaa]">{resultNote}</div>
            </div>
            <div className="whitespace-nowrap text-[28px] font-medium text-primary max-[480px]:text-[22px]">
              {formatTwd(result.total)}
            </div>
          </div>

          <div className="mt-2.5 grid grid-cols-1 gap-0 overflow-hidden rounded-lg border-[0.5px] border-[#ebebf0] sm:grid-cols-3 sm:gap-2 sm:overflow-visible sm:border-0">
            <div className="flex items-center justify-between bg-white px-3.5 py-2.5 text-left sm:block sm:rounded-lg sm:bg-[#f7f7fb] sm:px-2 sm:py-2.5 sm:text-center">
              <div className="text-[11px] text-[#aaa] sm:mb-1">台幣貨款</div>
              <div className="text-sm font-medium">{formatTwd(result.base)}</div>
            </div>
            <div className="flex items-center justify-between border-t-[0.5px] border-[#f0f0f5] bg-white px-3.5 py-2.5 text-left sm:block sm:rounded-lg sm:border-0 sm:bg-[#f7f7fb] sm:px-2 sm:py-2.5 sm:text-center">
              <div className="text-[11px] text-[#aaa] sm:mb-1">加成金額</div>
              <div className="text-sm font-medium">{formatTwd(result.total - result.base)}</div>
            </div>
            <div className="flex items-center justify-between border-t-[0.5px] border-[#f0f0f5] bg-white px-3.5 py-2.5 text-left sm:block sm:rounded-lg sm:border-0 sm:bg-[#f7f7fb] sm:px-2 sm:py-2.5 sm:text-center">
              <div className="text-[11px] text-[#aaa] sm:mb-1">有效加成率</div>
              <div className="text-sm font-medium text-primary">+{markup.toFixed(1)}%</div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCopy}
            className={`mt-3 w-full rounded-lg border px-2.5 py-2.5 text-[13px] transition-all ${
              copyState === 'ok'
                ? 'border-ci-button bg-ci-button/22 text-[#0F6E56]'
                : copyState === 'fail'
                  ? 'border-ci-highlight bg-ci-highlight/14 text-[#A33]'
                  : 'border-[#d0d0e0] bg-white text-[#888]'
            }`}
          >
            {copyState === 'ok' ? '✓ 已複製到剪貼簿' : copyState === 'fail' ? '複製失敗，請手動選取' : '複製計算結果'}
          </button>
        </div>
      </div>
      <div className="mt-3.5 text-center text-[11px] text-[#ccc]">Teleworker · 境外服務收費計算機</div>
    </div>
  );
};

export default OverseasServiceCalculator;
